import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Error, LockBlack, MoreHoriz, People } from "@livechat/design-system-icons";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  DesignToken,
  Heading,
  Icon,
  RadiusToken,
  ShadowToken,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import TeamAvatarStack from "components/avatar-stack/TeamAvatarStack";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { SettingsTableColumn, SettingsTableRow } from "components/settings-table";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { AutomationsRoutes } from "routes/automations/constants";
import { validateActions } from "routes/automations/shared/validators";
import { selectAgentsAlphabetically, selectIsAdmin, selectLoggedInAgentPermission } from "store/entities/agents";
import { selectCannedResponsesArray } from "store/entities/canned-responses";
import { IMacro } from "store/entities/macros/interfaces";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically, selectTeamsByIds } from "store/entities/teams";
import { textEllipsisStyles, textMultilineEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

interface IProps {
  macro: IMacro;
  setDeleteMacroId: (id: string) => void;
  highlight?: boolean;
  displayMode: string;
  hasScrolled: boolean;
  onScroll: () => void;
}

export const MacroCard: FC<IProps> = ({ macro, setDeleteMacroId, highlight, displayMode, hasScrolled, onScroll }) => {
  const { id, name, description, agentId, teamIds } = macro;
  const userRole = useSelector(selectLoggedInAgentPermission);
  const history = useHistory();
  const isShared = !agentId;
  const isOwner = useSelector(selectIsAdmin);
  const allTeamIds = useSelector(selectTeamsByIds);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const teams = useSelector(selectTeamsAlphabetically);
  const agents = useSelector(selectAgentsAlphabetically);
  const cannedResponses = useSelector(selectCannedResponsesArray);
  const tags = useSelector(selectTagsAlphabetically);

  const disableActionMenu = !isOwner && isShared;

  const onEdit = (): void => {
    history.push(`${AutomationsRoutes.EditMacro}/${id}`);
    trackEngagement("Macros - listing: Edit macro clicked", EventPlace.Automations, {
      role: userRole,
    });
  };

  const onDuplicate = (): void => {
    history.push(`${AutomationsRoutes.AddMacro}/${id}`);
    trackEngagement("Macros - listing: Duplicate macro clicked", EventPlace.Automations, {
      role: userRole,
    });
  };

  const hasError = useMemo(() => {
    return validateActions(macro.actions, teams, agents, cannedResponses, tags).some(error => error);
  }, [macro.actions, teams, agents, cannedResponses, tags]);

  useEffect(() => {
    if (highlight && scrollAnchorRef.current && !hasScrolled) {
      scrollAnchorRef.current.scrollIntoView({
        inline: "center",
        behavior: "smooth",
      });
      scrollAnchorRef.current.focus();
      window.scrollBy(0, 1);
      onScroll();
    }
  }, [highlight, history, scrollAnchorRef, onScroll, hasScrolled]);

  if (displayMode === "list") {
    return (
      <Row
        onClick={() => history.push(`${AutomationsRoutes.EditMacro}/${id}`)}
        hasError={hasError}
        highlight={highlight}
      >
        <Column>
          <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)} ref={scrollAnchorRef}>
            {hasError && (
              <Tooltip
                triggerRenderer={() => <Icon source={Error} customColor={withVar(DesignToken.ContentBasicNegative)} />}
              >
                <Text>There are errors in this macro</Text>
              </Tooltip>
            )}
            <Text css={textEllipsisStyles}>{name}</Text>
          </Flex>
        </Column>
        <Column>
          {description ? (
            <Text css={textEllipsisStyles} title={description} data-testid={`macro-description-${description}`}>
              {description}
            </Text>
          ) : (
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>No description</Text>
          )}
        </Column>
        <Column>
          {!teamIds || teamIds.length === 0 ? (
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>All teams</Text>
          ) : (
            <LeftAlignedAvatarStack teamIds={teamIds} />
          )}
        </Column>
        <Column>
          <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>{isShared ? "Shared" : "Private"}</Text>
        </Column>
        <Column actionMenu>
          <StopPropagation>
            <ActionMenu
              options={[
                {
                  key: "edit",
                  element: <ActionMenuItem>Edit</ActionMenuItem>,
                  onClick: onEdit,
                },
                {
                  key: "duplicate",
                  element: <ActionMenuItem>Duplicate</ActionMenuItem>,
                  onClick: onDuplicate,
                },
                {
                  key: "delete",
                  element: <ActionMenuItem kind="warning">Delete</ActionMenuItem>,
                  onClick: () => setDeleteMacroId(id),
                },
              ]}
              triggerRenderer={
                <Button
                  kind="text"
                  disabled={disableActionMenu}
                  icon={<Icon source={MoreHoriz} />}
                  data-testid="action-menu-trigger-button"
                  size="compact"
                />
              }
            />
          </StopPropagation>
        </Column>
      </Row>
    );
  }

  return (
    <Flex
      id={macro.id}
      ref={scrollAnchorRef}
      tabIndex={-1}
      column
      padding={withVar(SpacingToken.Spacing3)}
      borderRadius={withVar(RadiusToken.Radius3)}
      border={`1px solid ${withVar(DesignToken.BorderBasicSecondary)}`}
      gap={withVar(SpacingToken.Spacing3)}
      onClick={() => history.push(`${AutomationsRoutes.EditMacro}/${id}`)}
      onMouseDown={e => e.preventDefault()}
      css={{
        cursor: "pointer",
        backgroundColor: hasError ? withVar(DesignToken.SurfaceAccentEmphasisMinNegative) : undefined,
        borderColor: hasError ? withVar(DesignToken.BorderBasicNegative) : undefined,
        "&:focus": {
          boxShadow: withVar(ShadowToken.Focus),
        },
        "&:hover": {
          borderColor: withVar(hasError ? DesignToken.BorderBasicNegative : DesignToken.BorderBasicHover),
        },
      }}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="xs" css={textEllipsisStyles} data-testid={`macro-name-${name}`}>
          {name}
        </Heading>
        <div onClick={e => e.stopPropagation()}>
          <ActionMenu
            options={[
              {
                key: "edit",
                element: <ActionMenuItem>Edit</ActionMenuItem>,
                onClick: onEdit,
              },
              {
                key: "duplicate",
                element: <ActionMenuItem>Duplicate</ActionMenuItem>,
                onClick: onDuplicate,
              },
              {
                key: "delete",
                element: <ActionMenuItem kind="warning">Delete</ActionMenuItem>,
                onClick: () => setDeleteMacroId(id),
              },
            ]}
            triggerRenderer={
              <Button
                kind="text"
                disabled={disableActionMenu}
                icon={<Icon source={MoreHoriz} />}
                data-testid="action-menu-trigger-button"
              />
            }
          />
        </div>
      </Flex>
      <Block minHeight="66px" flex="1">
        <Text
          css={[
            textMultilineEllipsisStyles(3),
            { color: withVar(description ? DesignToken.ContentBasicSecondary : DesignToken.ContentBasicDisabled) },
          ]}
          data-testid={`macro-description-${description}`}
        >
          {description || "No description"}
        </Text>
      </Block>
      <Flex gap={withVar(SpacingToken.Spacing2)} alignItems="center" minHeight="24px">
        <Icon
          source={isShared ? People : LockBlack}
          size="small"
          data-testid={isShared ? "macro-shared-icon" : "macro-private-icon"}
        />
        <LeftAlignedAvatarStack teamIds={teamIds === null ? allTeamIds : teamIds} />
      </Flex>
    </Flex>
  );
};

const Column: React.FC<{
  children: React.ReactNode;
  actionMenu?: boolean;
}> = ({ children, actionMenu }) => (
  <SettingsTableColumn
    paddingDesktop={actionMenu ? "0" : `${withVar(SpacingToken.Spacing3)} ${withVar(SpacingToken.Spacing5)}`}
    css={{
      minWidth: "0",
      ...(actionMenu && {
        display: "flex",
        alignItems: "center",
      }),
    }}
    className="settings-table-column"
  >
    {children}
  </SettingsTableColumn>
);

const Row: FC<{
  children: React.ReactNode;
  onClick: () => void;
  hasError?: boolean;
  highlight?: boolean;
}> = ({ children, onClick, hasError, highlight }) => (
  <SettingsTableRow
    css={css`
      cursor: pointer;
      & > div {
        background-color: ${hasError ? withVar(DesignToken.SurfaceAccentEmphasisMinNegative) : undefined};
      }
    `}
    onClick={onClick}
    highlight={highlight}
  >
    {children}
  </SettingsTableRow>
);

const StopPropagation: FC<{ children: React.ReactNode }> = ({ children, ...props }) => (
  <div onClick={e => e.stopPropagation()} {...props}>
    {children}
  </div>
);

const LeftAlignedAvatarStack = styled(TeamAvatarStack)`
  justify-content: flex-start;
  margin-left: ${withVar(SpacingToken.Spacing2)};
`;
